// src/utils/membershipPayment.js
// Client side of channel memberships (evergreen 3.5a) and team memberships
// (evergreen 3.5b).
//
// One module for every membership surface (the Join modal, Your memberships,
// Channel and Team memberships, the members-only chat chip, the team page,
// MyVideos, VideoWatch):
//  - thin wrappers for the memberships callables (functions/modules/
//    memberships.js). Each takes the server's input object and resolves the
//    server's data; payloads carry only the keys the server reads. The viewer
//    wrappers take {kind, targetId} ('channel' | 'team'); creatorUid alone
//    still means a channel, so every 3.5a call site keeps working.
//  - ONE error rule: membershipErrorKind() classifies by token (the message
//    prefix, 'membership-busy: ...'), membershipErrorMessage() shows the
//    server's sentence with the token stripped. After any failed mutating
//    call the surface refetches its own source of truth; err.details.reason
//    is only ever logged (logMembershipError). The team callables reuse
//    every token; there is no team-only token.
//  - useMyMembership(kind, targetId): the viewer's own pair doc
//    (channelMemberships/{creatorUid}_{me} or teamMemberships/{teamId}_{me}),
//    normalized once to milliseconds so no raw Timestamp reaches a screen.
//    Access is one number: activeUntil. useMyChannelMembership(uid) and
//    useMyTeamMembership(teamId) are its two named forms.
//  - useChatGate(gateKey): chatGates/{gateKey} (public; an error reads as no
//    gate, i.e. open, like the rule).
//  - join v2 (R6, STEP3_5A_TIERS_DESIGN 3.13.2 and 3.13.3): the join's own
//    payment step (mountMembershipElement: Stripe's Payment Element in
//    deferred-intent subscription mode, card only, Link hidden), its
//    confirmMembershipJoin call, and sendEmailConfirmation for the email
//    gate. The browser never gets the payment's client secret and never runs
//    the bank's check: when the bank wants one, the join continues on
//    Embedded Checkout (founder decision 10-18). The strings live in
//    membershipJoin.js.
//
// Fan copy stays money-free; the payer sees only their own price. "Join" and
// "Member", never "Subscribe" (that labels the Plans button and means Follow
// in chat). "Plan" is the platform product; a channel or team membership is
// never a plan. tsMs and deliveredPerks are copies of memberships.js's rules,
// so the client and the server read the same doc the same way.

import { useState, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../../firebaseConfig';
import { CHAT_GATE_NO_TIER_RANK } from './membershipTiers';

// The embedded checkout mount is target-agnostic (Rumbles, Super Crits and
// memberships share it): callbacks come in as refs, so a parent re-render
// never tears down the form while the payer is typing.
export { mountEmbeddedCheckout } from './rumblePayment';

// ---------------------------------------------------------------------------
// Shared rules (copies of memberships.js; change both together)
// ---------------------------------------------------------------------------

// Firestore Timestamp, a number of ms, or anything else -> ms (0 when absent).
// Timestamps never compare numerically with Date.now(), so every date read
// goes through here.
export function tsMs(v) {
  return typeof v === 'number' ? v : (v?.toMillis?.() ?? 0);
}

// What a member actually has: a member the creator removed keeps only the
// members-only videos of the month they paid for.
export function deliveredPerks(perks, removedByCreator) {
  const p = perks || {};
  return removedByCreator
    ? { badge: false, membersChat: false, membersVideos: p.membersVideos }
    : p;
}

// A uid a pair id can carry (memberships.js isMembershipUid): 1-128
// characters, no '_' or '/', not '.' or '..'. Anything else never has a pair
// doc, so the hook does not listen for it.
function isPairUid(s) {
  return typeof s === 'string' && s.length >= 1 && s.length <= 128
    && !s.includes('_') && !s.includes('/') && s !== '.' && s !== '..';
}

// A team id a team pair id can carry: the client mirror of memberships.js
// TEAM_ID_RE (itself the mirror of teamStreams.js TEAM_STREAM_ID_RE). Change
// all three together.
export const TEAM_ID_RE_CLIENT = /^[A-Za-z0-9]{10,40}$/;

// ---------------------------------------------------------------------------
// Copy helpers
// ---------------------------------------------------------------------------

// The fan-facing perk names, in display order. The join line (joinLine) is an
// option in the join panel, never a listed perk; its label is only for the
// creator's own perk switches.
export const LISTED_PERKS = ['badge', 'membersChat', 'membersVideos'];
export const PERK_LABELS = {
  badge: 'A member badge in chat',
  membersChat: 'Chat when the channel is members-only',
  membersVideos: 'Members-only videos',
  joinLine: 'Join shout-out in chat',
};

// The same tables by membership kind. The channel entries ARE the tables
// above. A team has no videos perk, so a team panel iterating
// LISTED_PERKS_BY_KIND.team can never list one.
export const LISTED_PERKS_BY_KIND = {
  channel: LISTED_PERKS,
  team: ['badge', 'membersChat'],
};
export const PERK_LABELS_BY_KIND = {
  channel: PERK_LABELS,
  team: {
    badge: 'A team badge in squad chat',
    membersChat: 'Chat when squad chat is members-only',
    joinLine: 'Join shout-out in squad chat',
  },
};

// A team's name as a noun inside a SENTENCE: "Team Nightfall". A name that
// already starts with "team" (any case, no word boundary) stays as it is, so
// "Team Team Liquid" and "Team TeamSpirit" never render; an empty name reads
// "this team". Headers and card titles show teams.name verbatim instead.
// billingNotices.js teamNoun is the server twin; change both together.
export function teamDisplayName(name) {
  const n = String(name || '').trim();
  if (!n) return 'this team';
  return /^team/i.test(n) ? n : 'Team ' + n;
}

// The chat user card's tenure line for a team badge (months from the badge;
// a missing count reads as new, never "undefined months").
export function teamMemberTenureLabel(n) {
  const months = Number(n) || 0;
  return months <= 1 ? 'New team member' : 'Team member for ' + months + ' months';
}

// 499 -> "$4.99" (the same rendering as the server's formatUsd).
export function formatUsd(cents) {
  return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
}

// 499 -> "$4.99 a month" (the payer's own price only).
export function formatMonthlyPrice(cents) {
  return `${formatUsd(cents)} a month`;
}

// ms -> "Oct 26, 2026" (local date); '' for a missing date.
export function formatMembershipDate(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

// Token -> kind, by raw.includes(token) (the house style). Order matters only
// where one token could contain another; none of these do.
const ERROR_TOKENS = [
  ['membership-self', 'self'],
  ['membership-unavailable', 'unavailable'],
  ['membership-closed', 'closed'],
  ['membership-exists', 'exists'],
  ['membership-canceling', 'canceling'],
  ['membership-past-due', 'past_due'],
  ['membership-processing', 'processing'],
  ['membership-busy', 'busy'],
  ['membership-limit', 'limit'],
  ['terms-required', 'terms'],
  ['renewal-consent-required', 'consent'],
  ['membership-price-changed', 'price_changed'],
  ['account-under-review', 'review'],
  ['membership-not-found', 'not_found'],
  ['membership-not-resumable', 'not_resumable'],
  ['membership-checkout', 'checkout'],
  ['membership-update', 'update'],
  ['membership-ineligible', 'ineligible'],
  // Tiers (R5, 5.11 F9): a switch refused within the hours before a renewal.
  ['membership-renewing', 'renewing'],
  // Join v2 (R6, 3.13.2, 3.13.3): the email gate (J6) and the wait (J10)
  // land the panel back on its J7 / J10 block; the card precheck refusals
  // (J4, J5, J5L) keep the payment step open for another card;
  // sendEmailConfirmation's refusals (J9r, the retry, J9w's support line).
  ['membership-email', 'email'],
  ['membership-wait', 'wait'],
  ['card-limit', 'card_limit'],
  ['card-blocked', 'card_blocked'],
  ['card-noncard', 'card_noncard'],
  ['email-limit', 'email_limit'],
  ['email-unavailable', 'email_unavailable'],
  ['email-missing', 'email_missing'],
];

/**
 * How a surface should treat a membership error:
 * 'self'|'unavailable'|'closed'|'exists'|'canceling'|'past_due'|'processing'|
 * 'busy'|'limit'|'terms'|'consent'|'price_changed'|'review'|'not_found'|
 * 'not_resumable'|'checkout'|'update'|'ineligible'|'renewing'|'email'|'wait'|
 * 'card_limit'|'card_blocked'|'card_noncard'|'email_limit'|
 * 'email_unavailable'|'email_missing'|'error'.
 * Any kind a surface does not handle shows membershipErrorMessage(err).
 */
export function membershipErrorKind(err) {
  const raw = String(err?.message || '');
  const hit = ERROR_TOKENS.find(([token]) => raw.includes(token));
  return hit ? hit[1] : 'error';
}

/**
 * The member-facing sentence: the server's sentence after any leading
 * lowercase token (membership-busy:, members-only:, ...), else the fallback
 * (bare codes such as 'internal' never reach the member).
 */
export function membershipErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const raw = String(err?.message || '').trim();
  const stripped = raw.replace(/^[a-z][a-z-]*:\s*/, '').trim();
  return /\s/.test(stripped) ? stripped : fallback;
}

// details.reason is for us, never for the member.
export function logMembershipError(where, err) {
  console.warn(`[membership] ${where} failed:`, err?.code || '', err?.message || '', err?.details?.reason || '');
}

// ---------------------------------------------------------------------------
// Callables (section 5). Each resolves the server's data.
// ---------------------------------------------------------------------------

async function call(name, payload) {
  const fn = httpsCallable(functions, name);
  const { data } = payload === undefined ? await fn() : await fn(payload);
  return data || null;
}

// Keep only defined keys: the server validates types, and an undefined key
// would reach it as null.
function defined(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => { if (obj[k] !== undefined) out[k] = obj[k]; });
  return out;
}

// tierKey (R5) rides along only when the caller passes one: Keep sends none,
// and the server reads a missing tier as before.
function consentPayload(renewalConsent) {
  if (!renewalConsent || typeof renewalConsent !== 'object') return undefined;
  return defined({
    accepted: renewalConsent.accepted === true,
    amountCents: Math.round(Number(renewalConsent.amountCents) || 0),
    tierKey: typeof renewalConsent.tierKey === 'string' && renewalConsent.tierKey ? renewalConsent.tierKey : undefined,
  });
}

// Bumps whenever setMyMembershipPrefs succeeds in this app session, so the
// chat's badge-claim gate can re-arm (13.2).
let prefsEpoch = 0;
export function membershipPrefsEpoch() {
  return prefsEpoch;
}

// The target of a viewer callable (the server's targetOf): {kind:'team',
// targetId} for a team; anything else is a channel, named by targetId or,
// as every 3.5a call site does, by creatorUid alone. A channel payload
// carries the uid under BOTH keys: the 3.5b targetOf reads targetId ??
// creatorUid, and a 3.5a revision (B1b-1 pinned back, allowed by 19.7 until
// a team billing doc exists) reads creatorUid only, so this bundle keeps
// working against either.
function targetArgs({ kind, targetId, creatorUid } = {}) {
  if (kind === 'team') return { kind: 'team', targetId };
  const t = targetId ?? creatorUid;
  return { kind: 'channel', targetId: t, creatorUid: t };
}

/**
 * The join panel ({available, reason, reasonToken, kind, targetId,
 * targetName, targetPath, tiers, announceOption, limit, viewer, ...}; for
 * old bundles also perks / priceCents / disclosure / consentText of the
 * lowest sellable tier; a team offer has no tiers: its one price rides
 * perks / priceCents / disclosure / consentText, plus rosterCount). Tiers
 * (R5, 2.7.1, channels only): tiers = [{tierKey, name, rank, priceCents,
 * perks, disclosure, consentText, withinLimit?}], sellable only, by rank;
 * viewer adds tierKey, nextTierKey, nextTierAtMs, periodPriceCents,
 * canSwitch, lastJoinRefused and switchOptions [{tierKey, name, priceCents,
 * kind, disclosure, consentText, countsCents, withinLimit}]; limit only in
 * 'limits' mode.
 */
export function getMembershipOffer({ kind, targetId, creatorUid } = {}) {
  return call('getMembershipOffer', defined(targetArgs({ kind, targetId, creatorUid })));
}

/**
 * Starts (or resumes) the join's payment step for a target (targetArgs:
 * kind 'team' + targetId, or a channel). tierKey = the tier picked (R5,
 * channels only; the legacy one-price panel and a team join send none);
 * renewalConsent = {accepted:true, amountCents, tierKey} for the price the
 * panel showed. flow 'element' (R6, 3.13.2) asks for our own payment step;
 * the server still decides:
 *  - flow 'element': {checkoutId, publishableKey, amountCents, disclosure,
 *    creatorName, tierKey, tierName, tierLabel, expiresAtMs,
 *    elementsOptions: {mode:'subscription', amount, currency,
 *    paymentMethodTypes:['card']}} and NO Checkout Session: the Payment
 *    Element mounts from it and confirmMembershipJoin pays;
 *  - flow 'checkout' (asked for: the join continuing after the bank wanted
 *    to confirm; or the server's emergency switch, the attempt limit, a
 *    Stripe credit balance, an older server): {clientSecret, publishableKey,
 *    disclosure, kind, targetId, targetName, tierKey, tierName, ...} for
 *    Stripe's Embedded Checkout, as before. Asking for 'checkout' sends no
 *    flow (the server's default).
 */
export async function createMembershipCheckout({
  kind, targetId, creatorUid, tierKey, announce, renewalConsent, flow,
} = {}) {
  const data = await call('createMembershipCheckout', defined({
    ...targetArgs({ kind, targetId, creatorUid }),
    tierKey: typeof tierKey === 'string' && tierKey ? tierKey : undefined,
    announce: announce === true,
    renewalConsent: consentPayload(renewalConsent),
    flow: flow === 'element' ? 'element' : undefined,
  }));
  if (data?.flow === 'element') {
    if (typeof data.checkoutId !== 'string' || !data.checkoutId || typeof data.publishableKey !== 'string' || !data.publishableKey) {
      throw new Error("membership-checkout: We couldn't start checkout. Please try again.");
    }
    return data;
  }
  if (!data?.clientSecret) {
    throw new Error("membership-checkout: We couldn't start checkout. Please try again.");
  }
  return { ...data, flow: 'checkout', publishableKey: data.publishableKey || null };
}

/**
 * Pays an element checkout (R6, 3.13.2): the server reads the
 * ConfirmationToken's card, checks it before any charge, creates the
 * subscription and confirms its first payment on the server. Resolves
 * {status: 'succeeded'|'processing'|'continue_checkout'|'declined'|
 * 'requires_payment_method'|'refused', reason?, message?}; never a client
 * secret. continue_checkout: the bank wants to confirm the payment, nothing
 * was charged and the server closed this payment step; the join continues
 * on Embedded Checkout. A token confirms once: send a new one for every new
 * attempt. Refusals throw (card-limit / card-blocked / card-noncard,
 * membership-email / membership-wait, membership-checkout = start again,
 * membership-closed = the payment step timed out or the channel closed
 * joins, ...).
 */
export function confirmMembershipJoin({ checkoutId, confirmationToken } = {}) {
  return call('confirmMembershipJoin', defined({
    checkoutId,
    confirmationToken: typeof confirmationToken === 'string' && confirmationToken ? confirmationToken : undefined,
  }));
}

/**
 * Sends OUR confirmation email (R6, 3.13.3; J11) to the account's own
 * sign-in address. returnTo is a route key the server maps to a fixed page
 * ('join' = the channel page of creatorUid, 'verification' = Verification
 * Center), never a URL. Resolves {sent:true, maskedEmail, cooldownMs} or
 * {sent:false, alreadyConfirmed:true, maskedEmail}. Refusals: email-limit
 * (J9r; details.retryAtMs), email-unavailable, email-missing.
 */
export function sendEmailConfirmation({ returnTo, creatorUid } = {}) {
  return call('sendEmailConfirmation', defined({
    returnTo: returnTo === 'join' ? 'join' : 'verification',
    creatorUid: returnTo === 'join' && typeof creatorUid === 'string' && creatorUid ? creatorUid : undefined,
  }));
}

// ---------------------------------------------------------------------------
// The join's payment step: Stripe's Payment Element (R6, 3.13.2)
// ---------------------------------------------------------------------------

// Card entry only: Link hidden (a Link payment can be funded with no card
// behind it, 3.13.1), and Apple Pay / Google Pay hidden until the wallet
// domains are confirmed for critcash.com (10.2).
export const MEMBERSHIP_PAYMENT_ELEMENT_OPTIONS = Object.freeze({
  layout: 'tabs',
  wallets: Object.freeze({ applePay: 'never', googlePay: 'never', link: 'never' }),
});

// The house dark card and gold (the join modal's colors).
const ELEMENT_APPEARANCE = {
  theme: 'night',
  variables: {
    colorPrimary: '#FCD000',
    colorBackground: '#151515',
    colorText: '#EDEDED',
    colorDanger: '#e05555',
    borderRadius: '8px',
  },
};

/**
 * The deferred-intent options the Element is created with: ONLY the server's
 * mode, amount, currency and payment method types (card), so the Element's
 * amount is the checkout's own and nothing else rides along. null when the
 * server's options are unusable.
 */
export function membershipElementsOptions(o) {
  const amount = Number(o?.amount);
  const types = Array.isArray(o?.paymentMethodTypes) ? o.paymentMethodTypes.filter((t) => t === 'card') : [];
  if (o?.mode !== 'subscription' || !Number.isInteger(amount) || amount <= 0 || o?.currency !== 'usd' || !types.length) return null;
  return { mode: 'subscription', amount, currency: 'usd', paymentMethodTypes: ['card'], appearance: ELEMENT_APPEARANCE };
}

const resolveContainer = (c) => (c && typeof c === 'object' && 'current' in c ? c.current : c);
const readCallback = (r) => (typeof r === 'function' ? r : (r && typeof r.current === 'function' ? r.current : null));

/**
 * Mounts the Payment Element for an element checkout and returns a
 * controller. Call it from an effect keyed on the checkout only and return
 * controller.destroy as the cleanup; callbacks come in as refs (read when
 * they fire), so a parent re-render never tears down the form.
 *
 * container: a DOM element or a ref to one (React Native Web: a View ref).
 * setup: { publishableKey, elementsOptions } from createMembershipCheckout.
 * onReadyRef: called once the form can be used.
 * onErrorRef: called with the error if the form cannot open.
 *
 * controller:
 *  - destroy()
 *  - clear(): empties the card fields ("Use another card")
 *  - createToken() -> {tokenId} | {error, validation}: elements.submit()
 *    first (validation errors show inside the form; validation true), then
 *    stripe.createConfirmationToken. Nothing is charged.
 */
export function mountMembershipElement(container, { publishableKey, elementsOptions } = {}, onReadyRef, onErrorRef) {
  let cancelled = false;
  let stripe = null;
  let elements = null;
  let element = null;
  const destroy = () => {
    cancelled = true;
    const current = element;
    element = null;
    elements = null;
    if (current) { try { current.destroy(); } catch (_) { /* noop */ } }
  };
  const fail = (err) => {
    if (cancelled) return;
    console.error('[membership] payment form failed to open:', err?.message || err);
    destroy();
    const onError = readCallback(onErrorRef);
    if (onError) onError(err);
  };
  const controller = {
    destroy,
    clear: () => {
      if (!element) return;
      try { element.clear(); element.focus(); } catch (_) { /* noop */ }
    },
    createToken: async () => {
      if (cancelled || !stripe || !elements) return { error: { type: 'not_ready' }, validation: false };
      const submitted = await elements.submit();
      if (submitted?.error) return { error: submitted.error, validation: true };
      const params = {};
      // The token's return address for a bank check, as Stripe.js takes it.
      // This page never runs a bank check (the server confirms; when the
      // bank wants to confirm, the join continues on Embedded Checkout).
      if (typeof window !== 'undefined' && /^https:|^http:\/\/localhost[:/]/.test(window.location?.href || '')) {
        params.return_url = window.location.href;
      }
      const created = await stripe.createConfirmationToken({ elements, params });
      if (created?.error) return { error: created.error, validation: false };
      const tokenId = created?.confirmationToken?.id;
      return typeof tokenId === 'string' && tokenId ? { tokenId } : { error: { type: 'no_token' }, validation: false };
    },
  };

  if (Platform.OS !== 'web') return controller;
  const options = membershipElementsOptions(elementsOptions);
  (async () => {
    try {
      if (typeof publishableKey !== 'string' || !publishableKey) throw new Error('Stripe publishable key is not configured.');
      if (!options) throw new Error('The payment options are not usable.');
      const { loadStripe } = await import('@stripe/stripe-js');
      const s = await loadStripe(publishableKey);
      if (cancelled) return;
      if (!s) throw new Error('Stripe failed to load.');
      stripe = s;
      elements = s.elements(options);
      const created = elements.create('payment', MEMBERSHIP_PAYMENT_ELEMENT_OPTIONS);
      element = created;
      created.on('ready', () => {
        if (cancelled) return;
        const onReady = readCallback(onReadyRef);
        if (onReady) onReady();
      });
      created.on('loaderror', (e) => fail(e?.error || e));
      const el = resolveContainer(container);
      if (!el) throw new Error('Payment form container is not mounted.');
      created.mount(el);
    } catch (err) {
      fail(err);
    }
  })();
  return controller;
}

export function listMyMemberships() {
  return call('listMyMemberships', {});
}

export function cancelMyMembership({ kind, targetId, creatorUid } = {}) {
  return call('cancelMyMembership', defined(targetArgs({ kind, targetId, creatorUid })));
}

export function resumeMyMembership({
  kind, targetId, creatorUid, renewalConsent,
} = {}) {
  return call('resumeMyMembership', defined({
    ...targetArgs({ kind, targetId, creatorUid }),
    renewalConsent: consentPayload(renewalConsent),
  }));
}

/**
 * Switch tiers at the next renewal, or undo a pending switch (R5, 2.6.2).
 * Channel memberships only: a team membership has one price and no tiers.
 * consent = {accepted:true, amountCents, tierKey} for the option shown.
 * Resolves {success, kind: 'switch'|'stay', tierKey, periodTierKey,
 * effectiveAtMs, amountCents}. Nothing is charged when it is made.
 */
export function switchMyMembershipTier({ creatorUid, tierKey, consent } = {}) {
  const c = consent && typeof consent === 'object' ? consent : {};
  return call('switchMyMembershipTier', {
    creatorUid,
    tierKey,
    consent: {
      accepted: c.accepted === true,
      amountCents: Math.round(Number(c.amountCents) || 0),
      tierKey: typeof c.tierKey === 'string' ? c.tierKey : tierKey,
    },
    platform: 'web',
  });
}

export async function setMyMembershipPrefs({
  kind, targetId, creatorUid, showBadge,
} = {}) {
  const data = await call('setMyMembershipPrefs', defined({
    ...targetArgs({ kind, targetId, creatorUid }),
    showBadge,
  }));
  prefsEpoch += 1;
  return data;
}

// The ONE badge callable for both kinds (no claimTeamMemberBadge): StreamChat
// calls claimMemberBadge({kind: badgeTarget.kind, targetId: badgeTarget.id}).
export function claimMemberBadge({ kind, targetId, creatorUid } = {}) {
  return call('claimMemberBadge', defined(targetArgs({ kind, targetId, creatorUid })));
}

export function getMyMembershipSettings({ blockedAfter } = {}) {
  return call('getMyMembershipSettings', defined({ blockedAfter }));
}

/**
 * Creator settings (R5, 2.7.3): {enabled?, tiers?: {[tierKey]: {on?,
 * priceCents?}}, perkTiers?: {badge?, membersChat?, membersVideos?} (a tier
 * key, or null = off), perks?: {joinLine?}}. Resolves the full settings
 * shape (getMyMembershipSettings).
 */
export function updateMyMembershipSettings({ enabled, tiers, perkTiers, perks } = {}) {
  return call('updateMyMembershipSettings', defined({ enabled, tiers, perkTiers, perks }));
}

export function endAllMyMemberships({ confirm } = {}) {
  return call('endAllMyMemberships', { confirm: confirm === true });
}

export function setChannelChatMembersOnly({ on, creatorUid } = {}) {
  return call('setChannelChatMembersOnly', defined({ on, creatorUid }));
}

export function setChannelMemberBlocked({ viewerUid, blocked } = {}) {
  return call('setChannelMemberBlocked', { viewerUid, blocked });
}

// ---------------------------------------------------------------------------
// Team memberships: the captain's settings screen and the public team page
// (3.5b 5.8-5.13). Every one is keyed by teamId; the captain test is the
// server's.
// ---------------------------------------------------------------------------

/**
 * The captain's settings payload ({kind:'team', teamId, teamName, isCaptain,
 * available, show, termsCurrent, eligibility, warnings, roster, settings,
 * rungs, sharePct, members, blocked, chat, ...}); a non-captain gets
 * {isCaptain:false, viewerOnRoster, eligibility:{ok:false, reason:
 * 'not_captain', message}, chat}.
 */
export function getMyTeamMembershipSettings({ teamId, blockedAfter } = {}) {
  return call('getMyTeamMembershipSettings', defined({ teamId, blockedAfter }));
}

/** Captain-only; resolves the same payload as getMyTeamMembershipSettings. */
export function updateMyTeamMembershipSettings({
  teamId, enabled, rungCents, perks,
} = {}) {
  return call('updateMyTeamMembershipSettings', defined({
    teamId, enabled, rungCents, perks,
  }));
}

/** {success, scheduled, remaining, checkouts:{expired, healed, overflow}} */
export function endAllMyTeamMemberships({ teamId, confirm } = {}) {
  return call('endAllMyTeamMemberships', defined({ teamId, confirm: confirm === true }));
}

/** {success, blocked} */
export function setTeamMemberBlocked({ teamId, viewerUid, blocked } = {}) {
  return call('setTeamMemberBlocked', defined({ teamId, viewerUid, blocked }));
}

/** {success, membersOnly}; members-only squad chat (chatGates/{teamId}). */
export function setTeamChatMembersOnly({ teamId, on } = {}) {
  return call('setTeamChatMembersOnly', defined({ teamId, on }));
}

/**
 * The public team page (signed out allowed): {teamId, name, tag, logoUrl,
 * memberCount, disbanded, disbanding, disbandAtMs, roster, squad,
 * nextStreamAtMs, membershipsEnabled, membershipSelf?,
 * teamMembersOnlyChatOn, viewerOnRoster}. A missing team resolves
 * {disbanded:true, ...}, never a not-found error.
 */
export function getTeamPage({ teamId } = {}) {
  return call('getTeamPage', defined({ teamId }));
}

// Admin tools (the server checks the caller; each writes an audit row). A
// team end names {kind:'team', targetId: teamId}; a channel end still takes
// creatorUid. A withhold takes a rowId, or a paymentIntentId for every team
// share of one payment.
export function adminEndMembership({
  kind, targetId, creatorUid, viewerUid, subscriptionId, ground, when, refundLatest, waiveRefund, note,
} = {}) {
  return call('adminEndMembership', defined({
    kind, targetId, creatorUid, viewerUid, subscriptionId, ground, when, refundLatest, waiveRefund, note,
  }));
}

export function adminReplayMembershipInvoice({ invoiceId, testMode, credit } = {}) {
  return call('adminReplayMembershipInvoice', defined({ invoiceId, testMode, credit }));
}

export function adminWithholdMembershipCredit({
  rowId, paymentIntentId, note, release,
} = {}) {
  return call('adminWithholdMembershipCredit', defined({
    rowId, paymentIntentId, note, release,
  }));
}

// ---------------------------------------------------------------------------
// useMyMembership (useMyChannelMembership, useMyTeamMembership)
// ---------------------------------------------------------------------------

const EMPTY_STATE = {
  loading: false, error: false, doc: null, isMember: false, canChatAsMember: false, isCanceling: false, status: null,
};
// Signed out, no kind or target, a malformed target, or the creator's own
// channel: no listener.
const NO_MEMBERSHIP = Object.freeze({ ...EMPTY_STATE });
// Auth still restoring: not known yet, so neither Member nor Join.
const AUTH_PENDING = Object.freeze({ ...EMPTY_STATE, loading: true });

// setTimeout's ceiling; a longer delay fires at once.
const MAX_TIMER_MS = 2 ** 31 - 1;

// The tier keys a pair doc may carry (memberships.js writes nothing else). A
// local list, so this module keeps its imports.
const PAIR_TIER_KEYS = ['spark', 'amped', 'overdrive', 'maxed'];
const pairTierKey = (v) => (PAIR_TIER_KEYS.includes(v) ? v : null);

function normalizeMembershipDoc(d) {
  const perks = d?.perks || {};
  const refused = d?.startRefused && typeof d.startRefused === 'object' ? d.startRefused : null;
  return {
    creatorUid: d?.creatorUid || null,
    viewerUid: d?.viewerUid || null,
    status: typeof d?.status === 'string' ? d.status : null,
    paidPeriods: Number(d?.paidPeriods) || 0,
    perks: {
      badge: perks.badge === true,
      membersChat: perks.membersChat === true,
      membersVideos: perks.membersVideos === true,
    },
    removedByCreator: d?.removedByCreator === true,
    activeUntilMs: tsMs(d?.activeUntil),
    renewsAtMs: tsMs(d?.renewsAt),
    endsAtMs: tsMs(d?.endsAt),
    endedAtMs: tsMs(d?.endedAt),
    memberSinceMs: tsMs(d?.memberSince),
    updatedAtMs: tsMs(d?.updatedAt),
    // Tiers (R5, 2.7.2): the tier whose perks the member has now, its rank
    // (missing = 1, as the rules read it) and a pending switch.
    tierKey: pairTierKey(d?.tierKey),
    tierRank: Number.isInteger(d?.tierRank) && d.tierRank >= 1 && d.tierRank <= 4 ? d.tierRank : 1,
    nextTierKey: pairTierKey(d?.nextTierKey),
    nextTierAtMs: tsMs(d?.nextTierAt),
    // A join refused for its card in the last 24 h (3.5.8): {reason, atMs,
    // cashCents, creditCents} (the card refund and the credit returned; null
    // when an older server did not record them).
    startRefused: refused && typeof refused.reason === 'string'
      ? {
        reason: refused.reason,
        atMs: Number(refused.atMs) || 0,
        cashCents: Number.isInteger(refused.cashCents) && refused.cashCents >= 0 ? refused.cashCents : null,
        creditCents: Number.isInteger(refused.creditCents) && refused.creditCents >= 0 ? refused.creditCents : null,
      }
      : null,
  };
}

// The pair collection by kind.
const PAIR_COLLECTIONS = { channel: 'channelMemberships', team: 'teamMemberships' };

/**
 * The pair doc path of (kind, targetId) for the viewer `me`, or null when no
 * pair doc can exist (and so no listener runs): a channel needs a pair uid
 * that is not the viewer's own; a team needs a TEAM_ID_RE_CLIENT id and has
 * no self test (a roster member's pair never exists; the team page hides
 * Join by viewerOnRoster). Exported for the harness only.
 */
export function membershipPairPath(kind, targetId, me) {
  if (kind !== 'channel' && kind !== 'team') return null;
  const coll = PAIR_COLLECTIONS[kind];
  if (!targetId || !me || !isPairUid(me)) return null;
  if (kind === 'channel') {
    if (targetId === me || !isPairUid(targetId)) return null;
  } else if (!(typeof targetId === 'string' && TEAM_ID_RE_CLIENT.test(targetId))) {
    return null;
  }
  return `${coll}/${targetId}_${me}`;
}

/**
 * The signed-in viewer's membership of one channel or team: kind 'channel'
 * (targetId = the creator's uid) or 'team' (targetId = the teamId). Call it
 * unconditionally at the top of a screen. Always returns {loading, error,
 * doc, isMember, canChatAsMember, isCanceling, status}, never null. On a
 * listener error it returns {loading:false, error:true}: membership is
 * UNKNOWN, so a consumer shows neither Member nor Join (never flips a member
 * to Join). A falsy kind or targetId, signed out, a malformed target or the
 * channel owner -> the constant object with no listener.
 */
export function useMyMembership(kind, targetId) {
  const [me, setMe] = useState(() => auth.currentUser?.uid || null);
  const [authReady, setAuthReady] = useState(() => !!auth.currentUser);
  useEffect(() => onAuthStateChanged(auth, (u) => {
    setMe(u ? u.uid : null);
    setAuthReady(true);
  }), []);

  // The full path, so a channel and a team with the same id never share a
  // snapshot.
  const key = membershipPairPath(kind, targetId, me);

  const [snap, setSnap] = useState({ key: null, loading: true, error: false, doc: null });
  const loggedRef = useRef(false);
  useEffect(() => {
    if (!key) return undefined;
    setSnap({ key, loading: true, error: false, doc: null });
    // key = '<collection>/<pair id>'; the pair id never holds a '/'.
    const cut = key.indexOf('/');
    const unsub = onSnapshot(
      doc(db, key.slice(0, cut), key.slice(cut + 1)),
      (s) => {
        setSnap({ key, loading: false, error: false, doc: s.exists() ? normalizeMembershipDoc(s.data()) : null });
      },
      (err) => {
        if (!loggedRef.current) {
          loggedRef.current = true;
          console.warn('[membership] pair listener failed:', err?.code || err?.message);
        }
        setSnap({ key, loading: false, error: true, doc: null });
      },
    );
    return () => unsub();
  }, [key]);

  const current = snap.key === key ? snap : null;
  const d = current?.doc || null;
  const activeUntilMs = d ? d.activeUntilMs : 0;

  // Re-evaluate once access runs out (only for a finite future end).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!key || !Number.isFinite(activeUntilMs)) return undefined;
    const left = activeUntilMs - Date.now();
    if (left <= 0) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), Math.min(left + 1000, MAX_TIMER_MS));
    return () => clearTimeout(t);
  }, [key, activeUntilMs, tick]);

  const isMember = !!d && d.status !== 'ended' && activeUntilMs > Date.now();
  const result = useMemo(() => {
    if (!current || current.loading) return { ...EMPTY_STATE, loading: true };
    if (current.error) return { ...EMPTY_STATE, error: true };
    return {
      loading: false,
      error: false,
      doc: d,
      isMember,
      canChatAsMember: isMember && d.removedByCreator !== true,
      isCanceling: isMember && d.status === 'canceling',
      status: d ? d.status : null,
    };
  }, [current, d, isMember]);

  if (!key) return authReady || !kind || !targetId ? NO_MEMBERSHIP : AUTH_PENDING;
  return result;
}

/** The viewer's membership of one channel (3.5a): useMyMembership('channel', uid). */
export function useMyChannelMembership(creatorUid) {
  return useMyMembership('channel', creatorUid);
}

/** The viewer's membership of one team: useMyMembership('team', teamId). */
export function useMyTeamMembership(teamId) {
  return useMyMembership('team', teamId);
}

// ---------------------------------------------------------------------------
// useChatGate
// ---------------------------------------------------------------------------

const NO_GATE = Object.freeze({ gate: null, loading: false, error: false });

/**
 * chatGates/{gateKey}: {kind, membersOnly, minTierRank, updatedAtMs}, or null
 * when there is no gate doc. kind is passed through as stored (missing =
 * channel, which the consumer decides); minTierRank (R5) is 1-4, missing = 1
 * as the rule reads it. An error reads as {gate:null, error:true}: open.
 */
export function useChatGate(gateKey) {
  const key = typeof gateKey === 'string' && gateKey && !gateKey.includes('/') ? gateKey : null;
  const [state, setState] = useState({ key: null, gate: null, loading: true, error: false });
  useEffect(() => {
    if (!key) return undefined;
    setState({ key, gate: null, loading: true, error: false });
    const unsub = onSnapshot(
      doc(db, 'chatGates', key),
      (s) => {
        const g = s.exists() ? (s.data() || {}) : null;
        setState({
          key,
          gate: g ? {
            kind: g.kind,
            membersOnly: g.membersOnly,
            // 1-4 = a tier; CHAT_GATE_NO_TIER_RANK (5) = the members-only chat perk is off.
            minTierRank: Number.isInteger(g.minTierRank) && g.minTierRank >= 1 && g.minTierRank <= CHAT_GATE_NO_TIER_RANK ? g.minTierRank : 1,
            updatedAtMs: tsMs(g.updatedAt),
          } : null,
          loading: false,
          error: false,
        });
      },
      (err) => {
        console.warn('[membership] chat gate listener failed:', err?.code || err?.message);
        setState({ key, gate: null, loading: false, error: true });
      },
    );
    return () => unsub();
  }, [key]);

  if (!key) return NO_GATE;
  if (state.key !== key) return { gate: null, loading: true, error: false };
  return { gate: state.gate, loading: state.loading, error: state.error };
}
